<script setup lang="ts">
import { ContentBlock } from '~/components/ui/content-block'
import { AlertCircle, LucideEye } from 'lucide-vue-next'
import { Alert, AlertDescription } from '~/components/ui/alert'

interface Props {
  title: string
  /** Replaces the default "<type> «<title>»" H1, e.g. with the section name on organization subpages */
  heading?: string
  organizationTitle: string
  type:
    | 'credit'
    | 'deposit'
    | 'vacancy'
    | 'money-transfer'
    | 'insurance'
    | 'microcredit'
    | 'card'
    | 'crypto'
  logo?: string
  logoRectangle?: string
  statusComments?: string[]
  updatedAt?: string
  viewsCount?: number
  isIslamic?: boolean
  microcreditTypeTitle?: string
  cryptoTypeTitle?: string
}

const props = defineProps<Props>()

const { t } = useI18n()

// Organization pages (microcredit, insurance, crypto) show a wide logo on the right,
// matching the bank page header layout; other types keep the small square logo on the left.
const isOrganizationHeader = computed(() =>
  ['microcredit', 'insurance', 'crypto'].includes(props.type)
)

const headerLogoSrc = computed(() => {
  if (isOrganizationHeader.value) return props.logoRectangle || props.logo
  if (props.type === 'money-transfer') return props.logo
  return undefined
})

const headerRowClass = computed(() =>
  isOrganizationHeader.value
    ? 'flex flex-col justify-between gap-3 sm:gap-6 md:flex-row-reverse'
    : 'flex flex-col gap-3 sm:gap-6 md:flex-row'
)

const getTypeLabel = () => {
  switch (props.type) {
    case 'credit':
      return props.isIslamic
        ? t('ui.about_page_header.product_types.credit_islamic')
        : t('ui.about_page_header.product_types.credit')
    case 'card':
      return t('ui.about_page_header.product_types.card')
    case 'deposit':
      return t('ui.about_page_header.product_types.deposit')
    case 'money-transfer':
      return t('ui.about_page_header.product_types.money_transfer')
    case 'insurance':
      return t('ui.about_page_header.product_types.insurance')
    case 'vacancy':
      return t('ui.about_page_header.product_types.vacancy')
    case 'crypto':
      return props.cryptoTypeTitle || t('ui.about_page_header.product_types.crypto')
    case 'microcredit':
      return props.microcreditTypeTitle || t('common.mfc')
    default:
      return t('ui.about_page_header.product_types.product_default')
  }
}
</script>

<template>
  <div class="space-y-5">
    <Alert v-if="statusComments?.length" variant="red">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>
        <div class="space-y-2">
          <div v-for="(comment, idx) in statusComments" :key="idx" v-hydrated-html="comment" class="mt-0" />
        </div>
      </AlertDescription>
    </Alert>

    <ContentBlock variant="white">
      <article class="space-y-6">
        <div :class="headerRowClass">
          <div
            v-if="headerLogoSrc"
            :class="isOrganizationHeader ? 'h-16 rounded-md' : 'w-24 rounded-md md:max-w-24'"
          >
            <NuxtImg
              :src="headerLogoSrc"
              :alt="title"
              :class="isOrganizationHeader ? 'h-full w-auto' : 'w-full'"
              fetchpriority="high"
            />
          </div>
          <div class="space-y-1">
            <Heading tag="h1" class="font-serif text-xl !leading-8 sm:!text-2xl md:!text-3xl">
              <template v-if="heading">{{ heading }}</template>
              <template v-else>{{ getTypeLabel() }} «{{ title }}»</template>
            </Heading>
            <div class="mt-1 text-sm leading-3 text-gray-600">
              <p>{{ organizationTitle }}</p>
            </div>
          </div>
        </div>
        <slot />
        <div
          v-if="updatedAt || viewsCount"
          class="flex justify-between border-t pt-6 align-middle text-sm"
        >
          <div v-if="updatedAt">{{ $t('common.info_updated') }} {{ updatedAt }}</div>
          <div v-if="viewsCount" class="flex items-center">
            <LucideEye class="mr-2" /> {{ viewsCount }}
          </div>
        </div>
      </article>
    </ContentBlock>
  </div>
</template>
